import React, { useState, useEffect } from 'react';
import { Clock, MousePointer, Scroll, ArrowUp } from 'lucide-react';

interface AnimationState {
  show: boolean;
  x: number;
  y: number;
  text?: string;
  icon?: 'timer' | 'click' | 'scroll' | 'jump';
}

const ScoreCounter: React.FC = () => {
  const [score, setScore] = useState(0);
  const [showAnimation, setShowAnimation] = useState(false);
  const [clickAnimation, setClickAnimation] = useState<AnimationState>({ show: false, x: 0, y: 0 });
  const [particleAnimations, setParticleAnimations] = useState<AnimationState[]>([]);
  const [scrollAnimations, setScrollAnimations] = useState<AnimationState[]>([]);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isAtBottom, setIsAtBottom] = useState(false);

  // Check if scrolled to bottom of ThreeSection
  useEffect(() => {
    const handleScroll = () => {
      const threeSection = document.getElementById('threejs-section');
      if (threeSection) {
        const sectionRect = threeSection.getBoundingClientRect();
        const sectionBottom = sectionRect.bottom;
        const windowHeight = window.innerHeight;
        const isAtBottom = sectionBottom <= windowHeight + 100; // 100px threshold
        setIsAtBottom(isAtBottom);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Increment score by 5 every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setScore(prev => prev + 5);
      setShowAnimation(true);
      
      // Hide animation after 1 second
      setTimeout(() => {
        setShowAnimation(false);
      }, 1000);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Handle button clicks for points
  useEffect(() => {
    const handleButtonClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if the clicked element is interactive
      const isInteractive = 
        target.tagName === 'BUTTON' || 
        target.closest('button') || 
        target.tagName === 'A' ||
        target.closest('a') ||
        target.getAttribute('role') === 'button' ||
        target.classList.contains('btn-primary') ||
        target.classList.contains('btn-secondary') ||
        target.closest('[role="button"]') ||
        target.closest('.btn-primary') ||
        target.closest('.btn-secondary');
      
      if (isInteractive) {
        // Check if it's a header navigation link
        const isHeaderLink = target.closest('header') && (target.tagName === 'A' || target.closest('a'));
        
        const points = isHeaderLink ? 100 : 25;
        const text = isHeaderLink ? '+100' : '+25';
        
        setScore(prev => prev + points);
        setClickAnimation({
          show: true,
          x: event.clientX,
          y: event.clientY,
          text: text,
          icon: 'click'
        });
        
        // Hide click animation after 1 second
        setTimeout(() => {
          setClickAnimation(prev => ({ ...prev, show: false }));
        }, 1000);
      }
    };

    document.addEventListener('click', handleButtonClick);
    return () => document.removeEventListener('click', handleButtonClick);
  }, []);

  // Handle scroll for +50 points
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollDifference = Math.abs(currentScrollY - lastScrollY);
      
      // Award points if scrolled more than 200px
      if (scrollDifference > 400) {
        setScore(prev => prev + 50);
        
        // Create a new scroll animation
        const newAnimation: AnimationState = {
          show: true,
          x: window.innerWidth - 100 + Math.random() * 40 - 20, // Randomize position slightly
          y: window.innerHeight / 2 + Math.random() * 100 - 50, // Randomize position slightly
          text: '+50',
          icon: 'scroll'
        };
        
        setScrollAnimations(prev => [...prev, newAnimation]);
        
        // Remove this specific animation after 1 second
        setTimeout(() => {
          setScrollAnimations(prev => prev.filter(anim => anim !== newAnimation));
        }, 1000);
        
        setLastScrollY(currentScrollY);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Handle spacebar jump events for +1000 points
  useEffect(() => {
    const handleJumpEvent = (_event: CustomEvent) => {
      setScore(prev => prev + 1000);
      
      // Create a new jump animation
      const newAnimation: AnimationState = {
        show: true,
        x: window.innerWidth / 2 + Math.random() * 200 - 100, // Center with some randomness
        y: window.innerHeight / 2 + Math.random() * 100 - 50, // Center with some randomness
        text: '+1000',
        icon: 'jump'
      };
      
      setParticleAnimations(prev => [...prev, newAnimation]);
      
      // Remove this specific animation after 1.5 seconds (longer for bigger points)
      setTimeout(() => {
        setParticleAnimations(prev => prev.filter(anim => anim !== newAnimation));
      }, 1500);
    };

    window.addEventListener('characterJump', handleJumpEvent as EventListener);
    return () => window.removeEventListener('characterJump', handleJumpEvent as EventListener);
  }, []);

  // Handle particle click events from CherryBlossomParticles
  useEffect(() => {
    const handleParticleClick = (event: CustomEvent) => {
      const { points, viewportX, viewportY } = event.detail;
      setScore(prev => prev + points);
      
      const newAnimation: AnimationState = {
        show: true,
        x: viewportX,
        y: viewportY,
        text: `+${points}`,
        icon: 'click'
      };
      
      setParticleAnimations(prev => [...prev, newAnimation]);
      
      // Remove this specific animation after 1 second
      setTimeout(() => {
        setParticleAnimations(prev => prev.filter(anim => anim !== newAnimation));
      }, 1000);
    };

    window.addEventListener('particleClick', handleParticleClick as EventListener);
    return () => window.removeEventListener('particleClick', handleParticleClick as EventListener);
  }, []);



  // Helper function to render the appropriate icon
  const renderIcon = (iconType: 'timer' | 'click' | 'scroll' | 'jump') => {
    switch (iconType) {
      case 'timer':
        return <Clock className="w-4 h-4" />;
      case 'click':
        return <MousePointer className="w-4 h-4" />;
      case 'scroll':
        return <Scroll className="w-4 h-4" />;
      case 'jump':
        return <ArrowUp className="w-4 h-4" />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className={`${isAtBottom ? 'absolute' : 'fixed'} ${isAtBottom ? 'bottom-4 right-4' : 'bottom-4 right-4'} z-50`}>
        <div className="bg-black border-2 border-green-500 rounded-lg px-4 py-2 shadow-lg shadow-green-500/20 relative transition-all duration-500">
          <div className="font-mono text-white text-sm font-bold tracking-wider">
            SCORE: {score.toString().padStart(6, '0')}
          </div>
          
                  {/* +5 Animation */}
        {showAnimation && (
          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 font-mono text-white font-bold text-lg animate-bounce flex items-center gap-1">
            <Clock className="w-4 h-4" />
            +5
          </div>
        )}
        </div>
      </div>

      {/* Click Animation */}
      {clickAnimation.show && (
        <div 
          className="fixed font-mono text-white font-bold text-xl animate-bounce pointer-events-none z-50 flex items-center gap-1 transition-all duration-500"
          style={{
            left: clickAnimation.x - 20,
            top: clickAnimation.y - 20
          }}
        >
          {clickAnimation.icon && renderIcon(clickAnimation.icon)}
          {clickAnimation.text || '+25'}
        </div>
      )}

      {/* Particle Animations */}
      {particleAnimations.map((animation, index) => (
        <div 
          key={`particle-${index}`}
          className="fixed font-mono text-white font-bold text-xl animate-bounce pointer-events-none z-50 flex items-center gap-1 transition-all duration-500"
          style={{
            left: animation.x - 20,
            top: animation.y - 20
          }}
        >
          {animation.icon && renderIcon(animation.icon)}
          {animation.text}
        </div>
      ))}

      {/* Scroll Animations */}
      {scrollAnimations.map((animation, index) => (
        <div 
          key={`scroll-${index}`}
          className="fixed font-mono text-white font-bold text-xl animate-bounce pointer-events-none z-50 flex items-center gap-1 transition-all duration-500"
          style={{
            left: animation.x - 20,
            top: animation.y - 20
          }}
        >
          {animation.icon && renderIcon(animation.icon)}
          {animation.text}
        </div>
      ))}
    </>
  );
};

export default ScoreCounter; 